import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader, PageSkeleton } from "@/components/ui";
import { DetailFacilityOutcome } from "@/components/tester/change-requests/detail-facility-outcome";
import { DetailHeader } from "@/components/tester/change-requests/detail-header";
import { DetailItems } from "@/components/tester/change-requests/detail-items";
import { DetailMessages } from "@/components/tester/change-requests/detail-messages";
import { DetailPhotos } from "@/components/tester/change-requests/detail-photos";
import { DetailProvenance } from "@/components/tester/change-requests/detail-provenance";
import { type DetailLink } from "@/components/tester/change-requests/detail-set";
import { DetailTimeline } from "@/components/tester/change-requests/detail-timeline";
import { useActiveMembership } from "@/hooks/use-company-context";
import { useCompanyChangeRequestSet } from "@/hooks/use-company-change-request-sets";

export const Route = createFileRoute("/_company/company/submissions/change-requests/$setId")({
  component: CompanyChangeRequestDetailPage,
});

const LINK_CLASS = "text-primary hover:underline";

/**
 * The company's own pages behind a section's links: a successor set opens
 * here too, a returned test opens as the archive's document, and the set's
 * conversation opens on the dispatch board, which reads any thread the
 * company can see by its id.
 */
const companyDetailLink: DetailLink = (target, label) => {
  switch (target.kind) {
    case "set":
      return (
        <Link
          to="/company/submissions/change-requests/$setId"
          params={{ setId: target.setId }}
          className={LINK_CLASS}
        >
          {label}
        </Link>
      );
    case "test":
      return (
        <Link
          to="/company/submissions/$submissionId"
          params={{ submissionId: target.submissionId }}
          className={LINK_CLASS}
        >
          {label}
        </Link>
      );
    case "conversation":
      return (
        <Link to="/company/dispatch" search={{ thread: target.threadId }} className={LINK_CLASS}>
          {label}
        </Link>
      );
  }
};

/**
 * One change-request set a member sent, read-only: the tester's detail
 * sections with no action — no withdraw, no resubmit, no Message utility; only
 * the tester who sent a request acts on it. The company's projection never
 * carries a letter code's text, only that the set arrived with one.
 */
function CompanyChangeRequestDetailPage() {
  const { t } = useTranslation(["change-requests", "company"]);
  const { setId } = Route.useParams();
  const active = useActiveMembership();
  const {
    data: set,
    isLoading,
    isError,
  } = useCompanyChangeRequestSet(active?.company_id ?? "", setId);

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  // As the archive's document detail renders every failure: visibility is an
  // undifferentiated 404, and a 403 has no page of its own on this portal.
  if (isError || set === undefined) {
    return (
      <div className="space-y-4 p-8">
        <PageHeader title={t("change-requests:company.detail.title")} />
        <p className="text-sm text-muted">{t("change-requests:company.detail.notFound")}</p>
        <Link
          to="/company/submissions"
          search={{ tab: "change-requests" }}
          className="text-sm text-primary hover:underline"
        >
          {t("company:submissions.detail.back")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8">
      <DetailHeader
        set={set}
        backTo="/company/submissions"
        backSearch={{ tab: "change-requests" }}
        backLabel={t("company:submissions.detail.back")}
      />
      <DetailFacilityOutcome set={set} />
      <DetailPhotos set={set} />
      <DetailItems set={set} link={companyDetailLink} audience="company" />
      {set.via_letter_code && (
        <p className="text-sm text-muted">{t("change-requests:company.detail.viaLetterCode")}</p>
      )}
      <DetailProvenance set={set} />
      <DetailTimeline set={set} audience="company" />
      <DetailMessages set={set} link={companyDetailLink} />
    </div>
  );
}
